// =====================================================
// DOCX GENERATOR — shared by both core (free) and pro bundles.
// Free tier: same full redline output + diagonal watermark.
// Pro tier: same output, no watermark, plus track-changes option.
//
// The redline package, in order: the revised draft's parts (output-package.ts), the body
// (body-writer.ts), the summary page, then the footnotes, comments, styles, relationships and
// revision ids the body needs (notes.ts, comments-part.ts, cross-rels.ts, revision-ids.ts) and
// the headers and footers (headers-footers.ts).
// =====================================================

import { W_NS, serializeNode } from './xml-helpers';
import { setTrackChangesMode, acceptIncomingInPara, acceptIncomingProps, peekRevId, setRevIdStart } from './docx-tracking';
import type { DiffResult, ParsedDocument } from '../types';
import JSZip from 'jszip';
import { buildSummaryModel, summaryDocx, countRevisionGroups } from './summary';
import { mergeOriginalOnlyStyles } from './merge-definitions';
import { endRecord, recSnapshot, stripLabels, attachRecord } from './change-record';
import { startWriting, writeBody } from './body-writer';
import { copyRevisedPackage, addTrackChangesParts, addCustomProperties } from './output-package';
import { numberBodyRevisions, maxRevisionIdOutsideBody, acceptForeignRevisionsInParts } from './revision-ids';
import { resolveBookmarks } from './bookmarks';
import { resolveNoteRefs, redlineChangedNotes, changedNotes } from './notes';
import { redlineHeaderFooters, headerFooterText, resolveSectionRefs } from './headers-footers';
import { diffAncillaryContent } from './ancillary';
import { resolveComments } from './comments-part';
import { resolveCrossRels } from './cross-rels';

export { recordChanges } from './body-writer';
export { diffAncillaryContent, differingTextBoxes } from './ancillary';
export { balanceCommentAnchors, balanceBookmarks } from './anchors';

function extractSectPr(xmlDoc: Document | null): string {
  const defaultSectPr = '<w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/></w:sectPr>';
  if (!xmlDoc) return defaultSectPr;
  const body = xmlDoc.getElementsByTagNameNS(W_NS, 'body')[0];
  if (!body) return defaultSectPr;
  for (const child of Array.from(body.childNodes)) {
    if ((child as Element).nodeType === 1 && (child as Element).localName === 'sectPr') {
      return serializeNode(child);
    }
  }
  const paras = body.getElementsByTagNameNS(W_NS, 'p');
  if (paras.length > 0) {
    const lastP = paras[paras.length - 1];
    const sectPrs = lastP.getElementsByTagNameNS(W_NS, 'sectPr');
    if (sectPrs.length > 0) {
      return serializeNode(sectPrs[0]);
    }
  }
  return defaultSectPr;
}

function getDocumentNamespaces(docXml: string): string {
  const match = docXml.match(/<w:document\b([^>]*)>/);
  if (match) {
    return match[1];
  }
  return '';
}

/** Inject header and footer references into a <w:sectPr> element */
function injectHeaderFooterRefs(sectPrXml: string, headerRId: string, footerRId: string): string {
  const headerRefXml = `<w:headerReference w:type="default" r:id="${headerRId}"/>`;
  const footerRefXml = `<w:footerReference w:type="default" r:id="${footerRId}"/>`;

  let result = sectPrXml;

  // Header reference
  if (result.includes('w:headerReference') && result.includes('w:type="default"')) {
    result = result.replace(/<w:headerReference[^/]*w:type="default"[^/]*\/>/, headerRefXml);
  } else {
    result = result.replace(/(<w:sectPr[^>]*>)/, `$1${headerRefXml}`);
  }

  // Footer reference
  if (result.includes('w:footerReference') && result.includes('w:type="default"')) {
    result = result.replace(/<w:footerReference[^/]*w:type="default"[^/]*\/>/, footerRefXml);
  } else {
    result = result.replace(/(<w:sectPr[^>]*>)/, `$1${footerRefXml}`);
  }

  return result;
}

export async function generateRedlineDocx(
  diffResult: DiffResult,
  revDocData: ParsedDocument,
  origDocData: ParsedDocument,
  useTrackChanges: boolean = false,
  origFileName?: string,
  revFileName?: string,
  addWatermark: boolean = false,
  licensee?: string,
  author?: string,
  combineMode: boolean = false,
  /** Options the comparison ran with, in words, for the summary page. */
  summaryOptions: string[] = [],
): Promise<Blob> {

  // Author for this run's tracked changes — user-supplied name, default "Fredline".
  const revAuthor = (author && author.trim()) || 'Fredline';

  // Combine ("track all changes") only applies to track-changes output.
  const combine = combineMode && useTrackChanges;

  // Compare (and markup) output carries Fredline's revisions only. The parser already
  // compared the documents' accepted text; accept their tracked changes in the XML too,
  // once, before anything is emitted, so that no path (rebuilt paragraphs build their
  // pPr and runs from it) copies another author's revision into the output, and a real
  // formatting difference is recorded under Fredline. Combine keeps them on purpose.
  const paragraphs = combine ? diffResult.paragraphs : diffResult.paragraphs.map(acceptIncomingInPara);

  // Set up mode
  const revDate = new Date().toISOString().replace(/\.\d+Z$/, 'Z');
  const hasDocxBase = revDocData.zip != null;
  // (revision ids: one space across the package, revision-ids.ts)
  startWriting(revDocData, origDocData, useTrackChanges, combine, revAuthor, revDate,
    await maxRevisionIdOutsideBody(revDocData.zip, origDocData.zip));

  const newZip = new JSZip();

  // Relationship IDs for watermark header + footer
  const headerRId = 'rIdWatermarkHdr';
  const footerRId = 'rIdWatermarkFtr';

  await copyRevisedPackage(newZip, revDocData, hasDocxBase, addWatermark, useTrackChanges, headerRId, footerRId);

  await addTrackChangesParts(newZip, revDocData, hasDocxBase, useTrackChanges, revAuthor, paragraphs);

  // ===== THE BODY =====
  const { bodyParts, formatBlocks, push: pushPart } = writeBody(diffResult, paragraphs, useTrackChanges, combine, addWatermark);

  // Add section properties (with watermark header+footer references if needed)
  let sectPr = extractSectPr(revDocData.xmlDoc);
  if (!combine) sectPr = acceptIncomingProps(sectPr);
  if (addWatermark) {
    sectPr = injectHeaderFooterRefs(sectPr, headerRId, footerRId);
  }
  // Mid-document section break: must be inside a paragraph's <w:pPr>, NOT as a body-level child.
  // Only the final sectPr in the document may be a direct child of <w:body>.
  // The section break implicitly causes a page break (nextPage is default type).
  pushPart(`<w:p><w:pPr>${sectPr}</w:pPr></w:p>`);

  // ===== SUMMARY PAGE =====
  // Ancillary content the body diff can't see: footnote/endnote bodies and
  // headers/footers. Detect changes here so the summary reports them (the note
  // bodies are additionally redlined in the notes parts further down).
  let changedFootnotes = new Map<string, string>(), changedEndnotes = new Map<string, string>();
  let hf = { redlined: 0, unredlined: 0, paired: 0 };
  let ancillary: Awaited<ReturnType<typeof diffAncillaryContent>> = { notes: [], headers: [], headerWarnings: 0 };
  let headerChanges: number | undefined;
  if (hasDocxBase && origDocData.zip) {
    changedFootnotes = await changedNotes(origDocData, revDocData, 'footnote', diffResult);
    changedEndnotes = await changedNotes(origDocData, revDocData, 'endnote', diffResult);
    // Redline changed headers/footers in place (writes the parts into newZip);
    // parts that differ but can't be safely redlined get a summary warning.
    hf = await redlineHeaderFooters(newZip, origDocData.zip, revDocData.zip, origDocData.docXml, revDocData.docXml);
    // safety net, for when the pairing found no headers or footers at all (their references
    // could not be read): compare every header and footer part. (Not otherwise: a part no
    // section references, or a first-page or even-page one that is switched off, is never
    // displayed, and comparing it gave false "could not be marked" warnings.)
    if (hf.redlined === 0 && hf.unredlined === 0 && !hf.paired) {
      const [oh, rh, of_, rf] = await Promise.all([
        headerFooterText(origDocData.zip, 'header'), headerFooterText(revDocData.zip, 'header'),
        headerFooterText(origDocData.zip, 'footer'), headerFooterText(revDocData.zip, 'footer'),
      ]);
      if (oh !== rh) hf.unredlined++;
      if (of_ !== rf) hf.unredlined++;
    }
    ancillary = await diffAncillaryContent(origDocData, revDocData, diffResult);
    ancillary.headerWarnings = hf.unredlined;
    // tracked output: count the header and footer changes as written
    if (useTrackChanges) {
      headerChanges = 0;
      for (const name of Object.keys(newZip.files).filter(f => /^word\/(?:header|footer)\d*\.xml$/.test(f))) {
        headerChanges += countRevisionGroups(await newZip.file(name)!.async('string'), combine ? undefined : revAuthor);
      }
    }
  }
  const summary = await buildSummaryModel({
    diff: diffResult, orig: origDocData, rev: revDocData,
    origName: origFileName || 'Original', revName: revFileName || 'Revised', generatedAt: new Date(),
    output: combine ? 'combine' : useTrackChanges ? 'track' : 'markup', author: revAuthor, options: summaryOptions,
    ancillary, formatBlocks: useTrackChanges ? formatBlocks : undefined, headerChanges,
    record: recSnapshot(),
  });
  // the page's text width; a continuous section break needs a page break of its own
  const pgW = Number((/<w:pgSz\b[^>]*\bw:w="(\d+)"/.exec(sectPr) || [])[1] || 12240);
  const pgMar = (/<w:pgMar\b[^>]*>/.exec(sectPr) || [''])[0];
  const mar = (side: string, alt: string) => Number((new RegExp(`\\bw:(?:${side}|${alt})="(-?\\d+)"`).exec(pgMar) || [])[1] || 1440);
  const textWidth = pgW - mar('left', 'start') - mar('right', 'end');
  // Final sectPr: this IS the last section, so it's a direct body child (valid OOXML). It is the
  // summary page's: a tracked change to the section (Combine) stays with the document's last section.
  bodyParts.push(summaryDocx(summary, textWidth, { newPage: /<w:type w:val="continuous"\/>/.test(sectPr) }));
  pushPart(sectPr.replace(/<w:sectPrChange\b[\s\S]*?<\/w:sectPrChange>/g, ''));

  const defaultNs = ' xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas" xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:w10="urn:schemas-microsoft-com:office:word" xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml" xmlns:wp14="http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml" xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml" xmlns:w16cid="http://schemas.microsoft.com/office/word/2016/wordml/cid" xmlns:w16se="http://schemas.microsoft.com/office/word/2015/wordml/symex" xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape" xmlns:wpg="http://schemas.microsoft.com/office/word/2010/wordprocessingGroup" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" mc:Ignorable="w14 w15 w16cid w16se wp14"';
  const nsAttrs = (revDocData.docXml) ? getDocumentNamespaces(revDocData.docXml) : defaultNs;

  // the body's revisions numbered (revision-ids.ts)
  const BODY_BASE = 1000000;
  const numbered = numberBodyRevisions(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document${nsAttrs}>
  <w:body>
    ${bodyParts.join('')}
  </w:body>
</w:document>`, Math.max(BODY_BASE, peekRevId()));
  let documentXml = numbered.xml;
  if (peekRevId() >= BODY_BASE) setRevIdStart(numbered.next);

  // every footnote and endnote mark given its note (notes.ts)
  documentXml = await resolveNoteRefs(newZip, origDocData.zip, documentXml);

  newZip.file('word/document.xml', documentXml);

  await redlineChangedNotes(newZip, origDocData, hasDocxBase, changedFootnotes, changedEndnotes);
  // every comment mark given its comment (comments-part.ts), one bookmark of each name (bookmarks.ts)
  await resolveComments(newZip, origDocData, revDocData);
  await resolveBookmarks(newZip);

  // ===== STYLES ONLY THE ORIGINAL DEFINES =====
  // Deleted content carried in from the original can name a style the revised draft's
  // styles.xml lacks (a deleted table's table style, a deleted comment mark's
  // CommentReference): copy those definitions in (core/merge-definitions.ts).
  if (hasDocxBase && origDocData.zip) await mergeOriginalOnlyStyles(newZip, origDocData.zip);

  // a section break from the original: its headers and footers (headers-footers.ts)
  await resolveSectionRefs(newZip, origDocData.zip, revDocData.zip);
  await addCustomProperties(newZip, addWatermark, licensee);

  // ===== RESOLVE CROSS-DOCUMENT RELATIONSHIP TAGS =====
  // Orig-sourced XML was emitted with FRO- prefixed r:id/r:embed/r:link values
  // (see tagCrossRelIds). Import each referenced relationship (and any internal
  // target part) from the original into the corresponding output part's rels,
  // under a freshly minted id; strip constructs whose relationship can't be
  // resolved. Runs for every part Fredline assembles or merges.
  // Runs even when the revised base isn't a .docx (minimal package): tagged ids
  // must NEVER leak — with no source zip the constructs are stripped instead.
  for (const pn of ['word/document.xml', 'word/footnotes.xml', 'word/endnotes.xml', 'word/comments.xml']) {
    await resolveCrossRels(newZip, pn, (origDocData && origDocData.zip) || null);
  }

  // ===== OTHER AUTHORS' REVISIONS IN NOTES / COMMENTS / HEADERS / FOOTERS =====
  // Compare and markup carry only this run's revisions (markup: none at all). Combine keeps them.
  if (!combine) await acceptForeignRevisionsInParts(newZip, revAuthor, revDate, useTrackChanges);

  // Reset module state
  setTrackChangesMode(false);

  // The change record: the labelled body, then the labels off (the file is saved without them)
  const labeledBody: string = await newZip.file('word/document.xml')!.async('string');
  const savedBody = stripLabels(labeledBody);
  if (savedBody !== labeledBody) newZip.file('word/document.xml', savedBody);
  const recorded = endRecord();

  // Compressed like Word's own files (JSZip's default is STORE: a 37 KB input came out near 1 MB).
  // DEFLATE is deterministic, so the same inputs and clock still give byte-identical output.
  const blob: Blob = await newZip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  attachRecord(blob, { changes: recorded.changes, labeledBody });
  return blob;
}
